import React from 'react';
import { Box, Grid, TextField, Typography, MenuItem } from '@mui/material';
import { UserWizardData } from '../../../types/user.types';
import type { Location } from '../../../api';
import { FlagSwitch, PhoneNumberInput } from '../../../components/ui';

interface WizardStep1Props {
  formData: UserWizardData;
  onChange: (name: keyof UserWizardData, value: string | boolean | number | null) => void;
  errors?: Record<string, string>;
  isEditMode?: boolean;
  locations?: Location[];
  loadingLocations?: boolean;
}

const WizardStep1: React.FC<WizardStep1Props> = ({
  formData,
  onChange,
  errors,
  isEditMode = false,
  locations = [],
  loadingLocations = false,
}) => {
  return (
    <Box component="form" autoComplete="off" onSubmit={(e) => e.preventDefault()} sx={{ py: 2 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
        Información Básica
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        {isEditMode
          ? 'Define los datos del usuario y su estado de acceso'
          : 'Define los datos del usuario. Se generará una contraseña temporal única; al primer ingreso deberá cambiarla.'}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Correo"
            name="email"
            value={formData.email}
            onChange={(e) => onChange('email', e.target.value)}
            error={!!errors?.email}
            helperText={errors?.email || 'Correo único del usuario'}
            placeholder="correo@empresa.com"
            InputLabelProps={{ shrink: true }}
            autoComplete="off"
            inputProps={{
              autoComplete: 'off',
              'data-lpignore': 'true',
              'data-1p-ignore': 'true',
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Teléfono
            </Typography>
            <PhoneNumberInput
              value={formData.phone || ''}
              onChange={(value) => onChange('phone', value)}
              nationalNumberLabel="Número"
              nationalNumberPlaceholder="Ej. 71234567"
              textFieldProps={{
                error: !!errors?.phone,
                helperText: errors?.phone || 'Opcional',
                sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Nombre"
            name="firstName"
            value={formData.firstName}
            onChange={(e) => onChange('firstName', e.target.value)}
            error={!!errors?.firstName}
            helperText={errors?.firstName || 'Mínimo 2 caracteres'}
            placeholder="Nombre"
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Apellido"
            name="lastName"
            value={formData.lastName}
            onChange={(e) => onChange('lastName', e.target.value)}
            error={!!errors?.lastName}
            helperText={errors?.lastName || 'Mínimo 2 caracteres'}
            placeholder="Apellido"
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            required
            label="Sucursal Asignada"
            name="primaryLocationId"
            value={formData.primaryLocationId ?? ''}
            onChange={(e) => {
              const value = e.target.value === '' ? null : Number(e.target.value);
              onChange('primaryLocationId', value);
            }}
            error={!!errors?.primaryLocationId}
            helperText={
              errors?.primaryLocationId ||
              'Sucursal principal: ventas, inventario de sucursal y filtros por defecto'
            }
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            disabled={loadingLocations}
          >
            <MenuItem value="" disabled>
              {loadingLocations
                ? 'Cargando sucursales...'
                : locations.length === 0
                  ? 'Sin sucursales disponibles'
                  : 'Seleccionar sucursal'}
            </MenuItem>
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <FlagSwitch
            checked={!!formData.canSellFromOtherLocations}
            onChange={(e, checked) => onChange('canSellFromOtherLocations', checked)}
            activeColor="info"
            showLabel
            label="Ventas multi-sucursal: vender en cualquier sucursal y ver todas las órdenes"
          />
        </Grid>
        <Grid item xs={12}>
          <FlagSwitch
            checked={formData.isActive}
            onChange={(e, checked) => onChange('isActive', checked)}
            activeColor="success"
            showLabel
            label={formData.isActive ? 'Usuario activo' : 'Usuario inactivo'}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default WizardStep1;
